import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();
const USER_CACHE_KEY = 'userSnapshot';

const readCachedUser = () => {
  try {
    const cachedValue = localStorage.getItem(USER_CACHE_KEY);
    return cachedValue ? JSON.parse(cachedValue) : null;
  } catch (error) {
    localStorage.removeItem(USER_CACHE_KEY);
    return null;
  }
};

const persistCachedUser = (user) => {
  if (!user) {
    localStorage.removeItem(USER_CACHE_KEY);
    return;
  }

  localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
};

const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem(USER_CACHE_KEY);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      persistCachedUser(null);
      return null;
    }
    const userData = await authAPI.getCurrentUser();
    setUser(userData);
    persistCachedUser(userData);
    return userData;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const cachedUser = readCachedUser();
      if (cachedUser) {
        setUser(cachedUser);
        setLoading(false);
        refreshUser().catch(() => {
          clearSession();
          setUser(null);
        });
        return;
      }

      refreshUser()
        .then(userData => {
          setUser(userData);
        })
        .catch(() => {
          clearSession();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user } = response;
      
      localStorage.setItem('token', token);
      persistCachedUser(user);
      setUser(user);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      if (response?.token && response?.user) {
        localStorage.setItem('token', response.token);
        persistCachedUser(response.user);
        setUser(response.user);
      }

      return {
        success: true,
        pendingApproval: Boolean(response?.pendingApproval),
        message: response?.message,
      };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    clearSession();
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
